/**
 * Service Actions Hook (New Backend)
 * Complete service FSM operations via REST endpoints.
 * 
 * Service FSM States: pending → accepted → scheduled → in_progress → done → completed
 * Alternative paths: cancelled, on_hold
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  acceptService,
  declineService,
  scheduleService,
  startService,
  markServiceDone,
  completeService,
  cancelService,
  holdService,
  resumeService,
  assignProvider,
  entityAssignProvider,
  type ScheduleServiceInput,
  type AssignProviderInput,
} from '@/api/serviceActions'
import { dashboardKeys } from '@/hooks/useDashboardStats'

export function useServiceActions() {
  const queryClient = useQueryClient()

  const invalidateAll = async () => {
    // Refetch dashboard so Action Required / Upcoming lists update immediately (no page refresh)
    await queryClient.refetchQueries({ queryKey: dashboardKeys.all })
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['orders'] }),
      queryClient.invalidateQueries({ queryKey: ['order'] }),
    ])
  }

  // Accept service (pending → accepted)
  const acceptMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      const res = await acceptService(serviceId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to accept service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service accepted')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to accept service', { description: err?.message })
    },
  })

  // Decline service (pending → declined)
  const declineMutation = useMutation({
    mutationFn: async ({ serviceId, reason }: { serviceId: string; reason?: string }) => {
      const res = await declineService(serviceId, reason)
      if (res?.success === false) throw new Error(res?.message || 'Failed to decline service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service declined')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to decline service', { description: err?.message })
    },
  })

  // Schedule service (accepted → scheduled)
  const scheduleMutation = useMutation({
    mutationFn: async ({ serviceId, input }: { serviceId: string; input: ScheduleServiceInput }) => {
      const res = await scheduleService(serviceId, input)
      if (res?.success === false) throw new Error(res?.message || 'Failed to schedule service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service scheduled')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to schedule service', { description: err?.message })
    },
  })

  // Start service (scheduled → in_progress)
  const startMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      const res = await startService(serviceId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to start service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service started')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to start service', { description: err?.message })
    },
  })

  // Mark service done (in_progress → done)
  const markDoneMutation = useMutation({
    mutationFn: async ({ serviceId, notes }: { serviceId: string; notes?: string }) => {
      const res = await markServiceDone(serviceId, notes)
      if (res?.success === false) throw new Error(res?.message || 'Failed to mark service done')
      return res
    },
    onSuccess: async () => {
      toast.success('Service marked as done')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to mark service done', { description: err?.message })
    },
  })

  // Complete service (done → completed)
  const completeMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      const res = await completeService(serviceId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to complete service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service completed')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to complete service', { description: err?.message })
    },
  })

  // Cancel service
  const cancelMutation = useMutation({
    mutationFn: async ({ serviceId, reason }: { serviceId: string; reason?: string }) => {
      const res = await cancelService(serviceId, reason)
      if (res?.success === false) throw new Error(res?.message || 'Failed to cancel service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service cancelled')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to cancel service', { description: err?.message })
    },
  })

  // Hold service
  const holdMutation = useMutation({
    mutationFn: async ({ serviceId, reason }: { serviceId: string; reason?: string }) => {
      const res = await holdService(serviceId, reason)
      if (res?.success === false) throw new Error(res?.message || 'Failed to hold service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service put on hold')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to hold service', { description: err?.message })
    },
  })

  // Resume service from hold
  const resumeMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      const res = await resumeService(serviceId)
      if (res?.success === false) throw new Error(res?.message || 'Failed to resume service')
      return res
    },
    onSuccess: async () => {
      toast.success('Service resumed')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to resume service', { description: err?.message })
    },
  })

  // Assign provider to service
  const assignProviderMutation = useMutation({
    mutationFn: async ({ serviceId, input }: { serviceId: string; input: AssignProviderInput }) => {
      const res = await assignProvider(serviceId, input)
      if (res?.success === false) throw new Error(res?.message || 'Failed to assign provider')
      return res
    },
    onSuccess: async () => {
      toast.success('Provider assigned')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to assign provider', { description: err?.message })
    },
  })

  // Entity dispatch: assign provider + accept on their behalf in one atomic call
  const entityAssignProviderMutation = useMutation({
    mutationFn: async ({ serviceId, input }: { serviceId: string; input: AssignProviderInput }) => {
      const res = await entityAssignProvider(serviceId, input)
      if (res?.success === false) throw new Error(res?.message || 'Failed to assign provider')
      return res
    },
    onSuccess: async () => {
      toast.success('Provider assigned & service accepted')
      await invalidateAll()
    },
    onError: (err: any) => {
      // Backend returns structured errors { error, code }; surface the human message.
      const message = err?.response?.data?.error || err?.message
      toast.error('Failed to assign provider', { description: message })
    },
  })

  return {
    // Action functions (return void promises for easy chaining)
    acceptService: (serviceId: string) => acceptMutation.mutateAsync(serviceId).then(() => {}),
    declineService: (serviceId: string, reason?: string) =>
      declineMutation.mutateAsync({ serviceId, reason }).then(() => {}),
    scheduleService: (serviceId: string, input: ScheduleServiceInput) =>
      scheduleMutation.mutateAsync({ serviceId, input }).then(() => {}),
    startService: (serviceId: string) => startMutation.mutateAsync(serviceId).then(() => {}),
    markServiceDone: (serviceId: string, notes?: string) =>
      markDoneMutation.mutateAsync({ serviceId, notes }).then(() => {}),
    completeService: (serviceId: string) => completeMutation.mutateAsync(serviceId).then(() => {}),
    cancelService: (serviceId: string, reason?: string) =>
      cancelMutation.mutateAsync({ serviceId, reason }).then(() => {}),
    holdService: (serviceId: string, reason?: string) =>
      holdMutation.mutateAsync({ serviceId, reason }).then(() => {}),
    resumeService: (serviceId: string) => resumeMutation.mutateAsync(serviceId).then(() => {}),
    assignProvider: (serviceId: string, input: AssignProviderInput) =>
      assignProviderMutation.mutateAsync({ serviceId, input }).then(() => {}),
    entityAssignProvider: (serviceId: string, input: AssignProviderInput) =>
      entityAssignProviderMutation.mutateAsync({ serviceId, input }).then(() => {}),

    // Mutation objects (for loading states, etc.)
    acceptMutation,
    declineMutation,
    scheduleMutation,
    startMutation,
    markDoneMutation,
    completeMutation,
    cancelMutation,
    holdMutation,
    resumeMutation,
    assignProviderMutation,
    entityAssignProviderMutation,

    // Combined loading state
    isLoading:
      acceptMutation.isPending ||
      declineMutation.isPending ||
      scheduleMutation.isPending ||
      startMutation.isPending ||
      markDoneMutation.isPending ||
      completeMutation.isPending ||
      cancelMutation.isPending ||
      holdMutation.isPending ||
      resumeMutation.isPending ||
      assignProviderMutation.isPending ||
      entityAssignProviderMutation.isPending,
  }
}

